import { Injectable } from '@angular/core';
import { WebApiClient } from '../web-api-client.service';
import { WebApiService } from '../web-api.service';
import {ProductSearchCacheDto, ProductSearchResultDto, ProductSearchOptions} from 'src/app/shared/components/stocks/product-search/models/product-search';
import { ProductCombineCacheService } from './product-combine-cache.service';

/** 商品快取服務 */
@Injectable({ providedIn: 'root' })
export class ProductCacheService {
  private cache: ProductSearchCacheDto[] = null;
  private loading: Promise<void> = null;
  private api: WebApiService;

  constructor(
    private webApiClient: WebApiClient,
    private productCombineCacheService: ProductCombineCacheService
  ) {
    this.api = this.webApiClient.createHisService('stocks/ProductSearch');
  }

  /**
   * 載入快取資料
   * @param includeClinicPrice 是否包含診所層級價格 (預設 true)
   */
  async loadCache(includeClinicPrice: boolean = true): Promise<void> {
    // 已載入
    if (this.cache) {
      return;
    }

    // 正在載入中，等待完成
    if (this.loading) {
      return this.loading;
    }

    // 開始載入
    this.loading = this.api.get('GetCache', { includeClinicPrice }).toPromise()
      .then(async (data: ProductSearchCacheDto[]) => {
        this.cache = data || [];

        // 同時載入組合商品子項目定義
        await this.loadCombineDetailsCache();

        this.loading = null;
      })
      .catch(error => {
        console.error('載入商品快取失敗', error);
        this.cache = [];
        this.loading = null;
      });

    return this.loading;
  }

  /**
   * 載入組合商品子項目定義到快取
   */
  private async loadCombineDetailsCache(): Promise<void> {
    if (!this.cache) return;

    // 找出所有組合商品 (ProductType = 2)
    const combineProductIds = this.cache
      .filter(p => p.ProductType === 2)
      .map(p => p.Id);

    if (combineProductIds.length === 0) return;

    // 預載組合商品子項目定義
    await this.productCombineCacheService.preloadCombineDetails(combineProductIds);
  }

  /** 檢查快取是否已載入 */
  isCacheLoaded(): boolean {
    return this.cache !== null;
  }

  /** 搜尋商品 (本地過濾 + 展開) */
  search(keyword: string, options?: ProductSearchOptions): ProductSearchResultDto[] {
    if (!this.cache || !keyword || keyword.trim() === '') {
      return [];
    }

    const kw = keyword.trim().toUpperCase();
    const top = options?.top || 100;
    const result: ProductSearchResultDto[] = [];

    for (const product of this.cache) {
      // 排除指定商品
      if (options?.excludeProductIds?.includes(product.Id)) {
        continue;
      }

      // 商品類型篩選
      if (options?.productType !== null && options?.productType !== undefined) {
        if (product.ProductType !== options.productType) {
          continue;
        }
      }

      // 庫存類別篩選
      if (options?.stockType !== null && options?.stockType !== undefined) {
        if (product.StockType !== options.stockType) {
          continue;
        }
      }

      // 是否計算庫存篩選
      if (options?.isInventory === true) {
        if (!product.IsInventory) {
          continue;
        }
      }

      // 使用批號篩選 (true=只搜尋使用批號的商品)
      if (options?.isUseBatchNo === true) {
        if (!product.IsUseBatchNo) {
          continue;
        }
      }

      // 進貨稅別篩選
      if (options?.stockTaxType !== null && options?.stockTaxType !== undefined) {
        if (product.StockTaxType !== options.stockTaxType) {
          continue;
        }
      }

      // 銷售稅別篩選
      if (options?.saleTaxType !== null && options?.saleTaxType !== undefined) {
        if (product.SaleTaxType !== options.saleTaxType) {
          continue;
        }
      }

      // 廠商篩選
      if (options?.vendorCode) {
        if (product.VendorCode !== options.vendorCode) {
          continue;
        }
      }

      // 排除停止退貨的商品
      if (options?.excludeStopReturn === true) {
        if (product.IsStopReturn) {
          continue;
        }
      }

      // 排除停止進貨的商品
      if (options?.excludeStopStock === true) {
        if (product.IsStopStock) {
          continue;
        }
      }

      // 排除停止銷售的商品
      if (options?.excludeStopSale === true) {
        if (product.IsStopSale) {
          continue;
        }
      }

      // 搜尋比對 (不分大小寫)
      const match =
        product.ProductCode?.toUpperCase().includes(kw) ||
        product.ProductName?.toUpperCase().includes(kw) ||
        product.VendorSelfCode?.toUpperCase().includes(kw) ||
        product.Barcodes?.some(b => b?.toUpperCase().includes(kw)) ||
        product.RxCodes?.some(r => r?.toUpperCase().includes(kw)) ||
        product.StdCodes?.some(s => s?.toUpperCase().includes(kw));

      if (!match) {
        continue;
      }

      // 展開邏輯：每個條碼/醫令代碼/健保碼各一筆
      const barcodes = product.Barcodes || [];
      const rxCodes = product.RxCodes || [];
      const stdCodes = product.StdCodes || [];

      if (barcodes.length === 0 && rxCodes.length === 0 && stdCodes.length === 0) {
        // 無條碼/醫令/健保碼 → 顯示一筆
        result.push(this.toResultDto(product, null, null, null));
      } else {
        // 每個條碼一筆
        for (const barcode of barcodes) {
          result.push(this.toResultDto(product, barcode, null, null));
          if (result.length >= top) break;
        }

        // 每個醫令代碼一筆
        if (result.length < top) {
          for (const rxCode of rxCodes) {
            result.push(this.toResultDto(product, null, rxCode, null));
            if (result.length >= top) break;
          }
        }

        // 每個健保碼一筆 (StdCode 獨立欄位，detail.StdCode 儲存)
        // 排除與 RxCodes 重複的健保碼，避免下拉顯示完全相同的兩列
        if (result.length < top) {
          const rxCodeSet = new Set(rxCodes.map(r => r?.toUpperCase()));
          for (const stdCode of stdCodes) {
            if (rxCodeSet.has(stdCode?.toUpperCase())) continue;
            result.push(this.toResultDto(product, null, null, stdCode));
            if (result.length >= top) break;
          }
        }
      }

      // 達到上限則停止
      if (result.length >= top) {
        break;
      }
    }

    return result;
  }

  /** 清除快取 */
  clearCache(): void {
    this.cache = null;
    this.loading = null;
    // 同時清除組合商品快取
    this.productCombineCacheService.clearCache();
  }

  /** 重新載入快取 */
  async refreshCache(): Promise<void> {
    this.clearCache();
    await this.loadCache();
  }

  /** 取得組合商品快取服務 (供外部預載庫存使用) */
  getCombineCacheService(): ProductCombineCacheService {
    return this.productCombineCacheService;
  }

  /** 取得快取資料筆數 */
  getCacheCount(): number {
    return this.cache?.length || 0;
  }

  /** 以商品 ID 查詢快取資料 */
  getProductById(productId: number): ProductSearchCacheDto | null {
    if (!this.cache) return null;
    return this.cache.find(p => p.Id === productId) || null;
  }

  /** 取得所有母商品 ID (StockType=2) */
  getParentProductIds(): number[] {
    if (!this.cache) return [];
    return this.cache.filter(p => p.StockType === 2).map(p => p.Id);
  }

  /** 轉換為搜尋結果 DTO */
  private toResultDto(product: ProductSearchCacheDto, barcode: string, rxCode: string, stdCode: string): ProductSearchResultDto {
    // 取得主條碼和主醫令 (陣列中的第一個)
    const primaryBarcode = product.Barcodes?.length > 0 ? product.Barcodes[0] : null;
    const primaryRxCode = product.RxCodes?.length > 0 ? product.RxCodes[0] : null;

    // 計算有效價格
    // 有效進價: 優先使用診所進價，否則使用公司進價
    const effectiveOrderPrice = product.StoreOrderPrice ?? product.OrderPrice ?? 0;
    // 有效售價: 優先使用診所售價，否則使用公司售價
    const effectiveSalePrice = product.StoreSalePrice ?? product.SalePrice ?? 0;
    // 有效單位成本: StockStore.UnitCost(>0) → StorePrice.OrderPrice(>0) → Product.OrderPrice
    // (診所進價為 0 視為未設定，往下帶公司進價；後端 ProductBatchAddService 同此邏輯)
    const effectiveUnitCost = (product.StockUnitCost && product.StockUnitCost > 0)
      ? product.StockUnitCost
      : (product.StoreOrderPrice && product.StoreOrderPrice > 0)
        ? product.StoreOrderPrice
        : (product.OrderPrice ?? 0);

    return {
      Id: product.Id,
      ProductCode: product.ProductCode,
      ProductName: product.ProductName,
      SalePrice: product.SalePrice,
      Unit: product.Unit,
      VendorSelfCode: product.VendorSelfCode,
      VendorCode: product.VendorCode,
      VendorName: product.VendorName,
      ProductType: product.ProductType,
      StockType: product.StockType,
      IsUseBatchNo: product.IsUseBatchNo,
      IsInventory: product.IsInventory,
      BarcodeOrRxCode: barcode || rxCode || stdCode,
      Barcode: barcode,
      RxCode: rxCode,
      StdCode: stdCode,
      PrimaryBarcode: primaryBarcode,
      PrimaryRxCode: primaryRxCode,
      StockTaxType: product.StockTaxType,
      SaleTaxType: product.SaleTaxType,
      OrderPrice: product.OrderPrice,
      IsOpenOrderPrice: product.IsOpenOrderPrice,
      IsOpenSalePrice: product.IsOpenSalePrice,
      IsDiscount: product.IsDiscount,
      Spec: product.Spec,
      // 診所層級價格
      StockUnitCost: product.StockUnitCost,
      StoreOrderPrice: product.StoreOrderPrice,
      StoreSalePrice: product.StoreSalePrice,
      // 計算後的有效價格
      EffectiveUnitCost: effectiveUnitCost,
      EffectiveOrderPrice: effectiveOrderPrice,
      EffectiveSalePrice: effectiveSalePrice
    };
  }
}